import type { HelpdeskPluginCatalogPage } from "@repo/shared-types/helpdesk-plugins";

export const HELPDESK_CATALOG_PAGE_RETENTION_MS = 30 * 60 * 1_000;
const CATALOG_PAGE_CAPACITY = 100;

type CatalogPageCache = {
  read: (key: string, load: () => Promise<HelpdeskPluginCatalogPage>) => Promise<HelpdeskPluginCatalogPage>;
};

export function catalogPageExpiresAt(page: HelpdeskPluginCatalogPage, loadedAt: number): number {
  return Math.min(loadedAt + HELPDESK_CATALOG_PAGE_RETENTION_MS, page.cacheExpiresAt === undefined ? Infinity : Date.parse(page.cacheExpiresAt));
}

export function normalizeCatalogPage(page: HelpdeskPluginCatalogPage, loadedAt: number, elapsedMs: number = 0): HelpdeskPluginCatalogPage {
  const { cacheTtlMs, ...localPage } = page;
  const expiresAt = cacheTtlMs === undefined ? catalogPageExpiresAt(localPage, loadedAt) : loadedAt + Math.min(HELPDESK_CATALOG_PAGE_RETENTION_MS, cacheTtlMs - Math.max(0, elapsedMs));
  if (!Number.isFinite(expiresAt) || expiresAt <= loadedAt) throw new Error("Catalog data expired before it could be used");
  return { ...localPage, cacheExpiresAt: new Date(expiresAt).toISOString() };
}

export function createCatalogPageCache(now: () => number = Date.now, capacity: number = CATALOG_PAGE_CAPACITY): CatalogPageCache {
  const pages = new Map<string, HelpdeskPluginCatalogPage>();
  const inFlight = new Map<string, Promise<HelpdeskPluginCatalogPage>>();
  return {
    read: (key, load) => {
      const cached = pages.get(key);
      if (cached !== undefined) {
        pages.delete(key);
        if (Date.parse(cached.cacheExpiresAt ?? "") > now()) {
          pages.set(key, cached);
          return Promise.resolve(cached);
        }
      }
      const existing = inFlight.get(key);
      if (existing !== undefined) return existing;
      const pending = load()
        .then((page) => {
          const retained = normalizeCatalogPage(page, now());
          pages.set(key, retained);
          while (pages.size > capacity) {
            const oldest = pages.keys().next().value;
            if (oldest === undefined) break;
            pages.delete(oldest);
          }
          return retained;
        })
        .finally(() => inFlight.delete(key));
      inFlight.set(key, pending);
      return pending;
    },
  };
}
